'use client'

import { useMarketBalances } from '@/hooks/wallets/use-market-balances'
import {
	CHAIN_CONFIG,
	type RelayQuoteResponse,
	type RelayStep,
	getRelayQuote,
	getRelayStatus,
} from '@/lib/api/relay'
import { authFetch } from '@/lib/auth/auth-fetch'
import { persistActiveBridge } from '@/lib/hyperliquid/active-bridge'
import { hlLog } from '@/lib/hyperliquid/debug'
import { arbitrumReadClient } from '@/lib/onchain/arbitrum-read-client'
import { executeWithRelayerFallback } from '@/lib/relayer-fallback'
import { ensureGasBalance } from '@/lib/solana/ensureGasBalance'
import { sendSponsoredCalls } from '@/lib/wallet-core/sponsored'
import { invalidateMarketBalances } from '@/lib/wallets/invalidation'
import {
	buildErc20TransferCall,
	deriveDepositWalletAddress,
	ensureDepositWalletReady,
	executeDepositWalletBatch,
} from '@/lib/wallets/polymarket-deposit'
import {
	type BridgePlatform,
	type TransferDirection,
	getPlatformAddress,
	getPlatformChainConfig,
	getPlatformDecimals,
	getTransferDirection,
} from '@/lib/wallets/registry'
import { useTrading } from '@/providers/TradingProvider'
import { useWallet } from '@/providers/WalletContext'
import { captureApiError } from '@/utils/sentry-helpers'
import {
	OperationType,
	type RelayClient,
	type SafeTransaction,
} from '@polymarket/builder-relayer-client'
import { PublicKey, TransactionMessage, VersionedTransaction } from '@solana/web3.js'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import { type Address, erc20Abi } from 'viem'
import { useHyperliquidWithdraw } from './useHyperliquidWithdraw'

export type { BridgePlatform, TransferDirection } from '@/lib/wallets/registry'

export type TransferStatus =
	| 'idle'
	| 'loading_quote'
	| 'quote_ready'
	| 'executing'
	| 'success'
	| 'error'

export interface TransferQuote {
	steps: RelayStep[]
	totalFeeUsd: string
	/**
	 * Relay-only fees (gas + relayer), excluding the $1 HL validator fee on
	 * withdrawals. Display this as "Relay Fee" — the HL fee gets its own
	 * line via `hlBridgeFeeUsd` when applicable.
	 */
	relayFeeUsd: string
	/**
	 * The $1 net fee Hyperliquid's bridge validators charge on withdraw3.
	 * Zero for any other direction (deposits don't pay this). Render as a
	 * separate "HL Bridge Fee" line so users see the breakdown rather than
	 * a single rolled-up figure mislabeled "Relay Fee".
	 */
	hlBridgeFeeUsd: string
	estimatedOutput: string
	timeEstimate: number
	inputAmount: string
	direction: TransferDirection
	fromPlatform: BridgePlatform
	toPlatform: BridgePlatform
	raw: RelayQuoteResponse
}

export interface TransferParams {
	amount: string
	fromPlatform: BridgePlatform
	toPlatform: BridgePlatform
	useMaxBalance?: boolean
	exactOutput?: boolean
}

function relayTxToSafeTx(data: {
	to?: string
	data?: string
	value?: string
}): SafeTransaction | null {
	if (!(data.to && data.data)) return null
	return {
		to: data.to,
		data: data.data,
		value: data.value || '0',
		operation: OperationType.Call,
	}
}

export function useInternalTransfer() {
	const [status, setStatus] = useState<TransferStatus>('idle')
	const [quote, setQuote] = useState<TransferQuote | null>(null)
	const [currentStepIndex, setCurrentStepIndex] = useState(0)
	const [error, setError] = useState<string | null>(null)
	const [txHash, setTxHash] = useState<string | null>(null)

	const queryClient = useQueryClient()
	const {
		eoaAddress,
		evmAccount,
		solanaAddress,
		walletClient,
		publicClient,
		polymarketSafe,
		solanaConnection,
		solanaSigner,
		polymarketActiveWallet,
	} = useWallet()
	const { funderAddress, safeAddress: resolvedSafeAddress } = useTrading()
	const hyperliquidWithdraw = useHyperliquidWithdraw()
	const { balances } = useMarketBalances(undefined, {
		runtime: { addressOverrides: { polymarket: funderAddress } },
	})

	// Source of truth for the Polymarket Safe across this hook. TradingProvider
	// resolves it for legacy users whose profile row no longer carries
	// polymarketSafe; only fall back to the WalletContext value for normal
	// legacy rows. When neither exists — or the wallet switcher has the
	// deposit wallet active — the user is on the deposit-wallet path.
	const polymarketSafeSourceAddress = (resolvedSafeAddress ??
		(polymarketActiveWallet === 'deposit' ? undefined : polymarketSafe)) as
		| `0x${string}`
		| undefined

	const polymarketBalance = balances.polymarket?.cash.amount ?? 0
	const kalshiBalance = balances.kalshi?.cash.amount ?? 0
	const predictfunBalance = balances.predictfun?.cash.amount ?? 0

	const getBalance = useCallback(
		(platform: BridgePlatform): number => balances[platform]?.cash.amount ?? 0,
		[balances]
	)

	const getRawBalance = useCallback(
		(platform: BridgePlatform): bigint => balances[platform]?.cash.raw ?? 0n,
		[balances]
	)

	const getUserAddress = useCallback(
		(platform: BridgePlatform): string | undefined =>
			getPlatformAddress(platform, {
				eoaAddress,
				solanaAddress,
				polymarketSafe,
				polymarketActiveWallet,
				addressOverrides: { polymarket: funderAddress },
			}),
		[eoaAddress, solanaAddress, polymarketSafe, polymarketActiveWallet, funderAddress]
	)

	// Fetch quote for internal transfer
	const fetchQuote = useCallback(
		async (params: TransferParams) => {
			// Bridging FROM Hyperliquid: source is the user's Arbitrum EOA
			// (where withdraw3 validators will credit USDC ~4min after sign).
			// Quote against Arbitrum origin; execute() handles the withdraw3
			// sig + credit wait + relay leg.
			//
			// Balance check still uses HL totals (the user's mental model is
			// "I have $X on HL"), but the Relay quote is denominated in
			// Arbitrum USDC because that's where the relayer will pick up
			// after validators land it.
			// Outgoing Polymarket bridges cannot execute Relay.link approvals from
			// the Polymarket Safe/deposit wallet: the Polymarket relayer only
			// allows approved spenders. Quote from the user's EOA instead; execute()
			// sweeps pUSD there immediately before running the Relay steps.
			// Same shape for Hyperliquid: withdraw3 lands USDC on the EOA on
			// Arbitrum, so the Relay quote must use the EOA as `user`.
			const fromAddr =
				(params.fromPlatform === 'polymarket' && params.toPlatform !== 'polymarket') ||
				params.fromPlatform === 'hyperliquid'
					? eoaAddress
					: getUserAddress(params.fromPlatform)
			const toAddr = getUserAddress(params.toPlatform)

			if (!(fromAddr && toAddr)) {
				throw new Error('Wallet not connected')
			}

			setStatus('loading_quote')
			setError(null)
			setQuote(null)

			try {
				const sourceRaw = getRawBalance(params.fromPlatform)
				const decimals = getPlatformDecimals(params.fromPlatform)

				let amountInSmallestUnit: string
				let amountNum: number

				if (params.useMaxBalance) {
					// Reserve 0.5% buffer to cover Relay fees/slippage/rounding.
					// Without this, bridge simulation fails (Alchemy paymaster returns 400).
					const maxRaw = (sourceRaw * 995n) / 1000n
					amountInSmallestUnit = maxRaw.toString()
					amountNum = Number(maxRaw) / 10 ** decimals
				} else {
					amountNum = Number.parseFloat(params.amount)
					if (Number.isNaN(amountNum) || amountNum <= 0) {
						throw new Error('Invalid amount')
					}
					const requestedRaw = BigInt(Math.floor(amountNum * 10 ** decimals))
					if (!params.exactOutput && requestedRaw > sourceRaw) {
						throw new Error('Insufficient balance')
					}
					amountInSmallestUnit = requestedRaw.toString()
				}

				// Hyperliquid charges $1 USDC at the bridge for every withdraw3.
				// The Relay quote needs to use the post-fee amount because that's
				// what actually lands on Arbitrum — but we DON'T overwrite
				// amountNum / inputAmount because execute() must pass the
				// PRE-fee amount to withdraw3 (validators subtract the fee
				// themselves on settlement). User sees the original amount in
				// "Sending"; net amount is reflected in `estimatedOutput`.
				if (params.fromPlatform === 'hyperliquid') {
					const HL_FEE_RAW = BigInt(1e6) // $1.00 in 6-decimal USDC
					const netRaw = BigInt(amountInSmallestUnit) - HL_FEE_RAW
					if (netRaw <= 0n) {
						throw new Error('Minimum Hyperliquid withdraw is $2 (covers $1 bridge fee + transfer).')
					}
					amountInSmallestUnit = netRaw.toString()
				}

				const origin = getPlatformChainConfig(params.fromPlatform)
				const destination = getPlatformChainConfig(params.toPlatform)

				const response = await getRelayQuote({
					user: fromAddr,
					originChainId: origin.chainId,
					destinationChainId: destination.chainId,
					originCurrency: origin.currency,
					destinationCurrency: destination.currency,
					amount: amountInSmallestUnit,
					recipient: toAddr,
					tradeType: params.exactOutput ? 'EXACT_OUTPUT' : 'EXACT_INPUT',
				})

				const gasUsd = Number.parseFloat(response.fees.gas?.amountUsd || '0')
				const relayerUsd = Number.parseFloat(response.fees.relayer?.amountUsd || '0')
				// For HL-origin bridges, fold the $1 validator bridge fee into
				// the total user-visible fee figure and stretch the time
				// estimate to cover the off-chain validator settlement window
				// (~3-4 min before Arbitrum sees the credit). For HL-DESTINATION
				// bridges (deposits), the Relay leg lands on Arbitrum in ~5-15s
				// but the auto-deposit watcher + Bridge2 + HL validator credit
				// adds another ~3-4 minutes before the funds are usable. Both
				// directions need the wait surfaced in `timeEstimate` — without
				// it the modal shows "~1s" and users think the bridge is done
				// while their HL balance still reads $0.
				const hlBridgeFeeUsd = params.fromPlatform === 'hyperliquid' ? 1 : 0
				const totalFeeUsd = (gasUsd + relayerUsd + hlBridgeFeeUsd).toFixed(2)
				const baseTime = response.details.timeEstimate || 5
				const hlValidatorWindowSec = 4 * 60
				const timeEstimate =
					params.fromPlatform === 'hyperliquid' || params.toPlatform === 'hyperliquid'
						? baseTime + hlValidatorWindowSec
						: baseTime

				const inputAmount = params.exactOutput
					? response.details.currencyIn.amountFormatted
					: amountNum.toFixed(2)

				// Round the destination amount to 2 decimals so the UI doesn't
				// render "+$10.949163" — Relay returns full-precision USDC
				// amountFormatted (6 decimals) which leaks token-precision into
				// what's meant to be a USD display.
				const estimatedOutput = Number.parseFloat(
					response.details.currencyOut.amountFormatted
				).toFixed(2)

				const transferQuote: TransferQuote = {
					steps: response.steps,
					totalFeeUsd,
					relayFeeUsd: (gasUsd + relayerUsd).toFixed(2),
					hlBridgeFeeUsd: hlBridgeFeeUsd.toFixed(2),
					estimatedOutput,
					timeEstimate,
					inputAmount,
					direction: getTransferDirection(params.toPlatform),
					fromPlatform: params.fromPlatform,
					toPlatform: params.toPlatform,
					raw: response,
				}

				setQuote(transferQuote)
				setStatus('quote_ready')
				return transferQuote
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to get quote'
				setError(message)
				setStatus('error')
				throw err
			}
		},
		[eoaAddress, getUserAddress, getRawBalance]
	)

	// Execute Solana transaction from relay steps
	const executeSolanaSteps = useCallback(
		async (steps: RelayStep[]): Promise<string | null> => {
			if (!(solanaConnection && solanaSigner && solanaAddress)) {
				throw new Error('Solana wallet not available')
			}

			await ensureGasBalance(solanaConnection, solanaAddress, solanaSigner)
			let hash: string | null = null

			for (const step of steps) {
				for (const item of step.items) {
					if (item.data?.instructions) {
						const { blockhash } = await solanaConnection.getLatestBlockhash()

						const instructions = (
							item.data.instructions as Array<{
								keys: Array<{ pubkey: string; isSigner: boolean; isWritable: boolean }>
								programId: string
								data: string
							}>
						).map(ix => ({
							keys: ix.keys.map(k => ({
								pubkey: new PublicKey(k.pubkey),
								isSigner: k.isSigner,
								isWritable: k.isWritable,
							})),
							programId: new PublicKey(ix.programId),
							data: Buffer.from(ix.data, 'hex'),
						}))

						const message = new TransactionMessage({
							payerKey: new PublicKey(solanaAddress),
							recentBlockhash: blockhash,
							instructions,
						}).compileToV0Message()

						const tx = new VersionedTransaction(message)
						const signedTx = await solanaSigner.addSignature(tx)

						let signature: string
						try {
							signature = await solanaConnection.sendRawTransaction(signedTx.serialize(), {
								skipPreflight: false,
								preflightCommitment: 'confirmed',
							})
						} catch (err) {
							const errorMessage = err instanceof Error ? err.message : String(err)
							if (
								errorMessage.includes(
									'Attempt to debit an account but found no record of a prior credit'
								)
							) {
								throw new Error(
									"We're having issues landing the transaction on Solana. The chain might be busy, please retry."
								)
							}
							throw err
						}
						for (let i = 0; i < 30; i++) {
							const txStatus = await solanaConnection.getSignatureStatus(signature)
							if (
								txStatus.value?.confirmationStatus === 'confirmed' ||
								txStatus.value?.confirmationStatus === 'finalized'
							) {
								break
							}
							if (txStatus.value?.err) {
								throw new Error(`Transaction failed: ${JSON.stringify(txStatus.value.err)}`)
							}
							await new Promise(r => setTimeout(r, 1000))
						}

						hash = signature
					}
				}
			}
			return hash
		},
		[solanaConnection, solanaSigner, solanaAddress]
	)

	const executePolygonEoaSteps = useCallback(
		async (steps: RelayStep[]): Promise<string | null> => {
			if (!(evmAccount && eoaAddress)) {
				throw new Error('Polygon wallet not available')
			}

			const ops: { target: `0x${string}`; data: `0x${string}`; value?: bigint }[] = []

			for (const step of steps) {
				for (const item of step.items) {
					if (item.data?.chainId === CHAIN_CONFIG.polygon.id && item.data?.to && item.data?.data) {
						ops.push({
							target: item.data.to as `0x${string}`,
							data: item.data.data as `0x${string}`,
							value: item.data.value ? BigInt(item.data.value) : undefined,
						})
					}
				}
			}

			if (ops.length > 0) {
				return sendSponsoredCalls({ chain: 'polygon', account: evmAccount, calls: ops })
			}

			return null
		},
		[evmAccount, eoaAddress]
	)

	const sweepPolymarketToEoa = useCallback(
		async (amountRaw: bigint, relayClient: RelayClient | null): Promise<void> => {
			if (!(walletClient && publicClient && eoaAddress)) {
				throw new Error('Wallet not connected')
			}
			if (amountRaw <= 0n) throw new Error('Invalid amount')

			if (polymarketSafeSourceAddress) {
				if (!relayClient) {
					throw new Error('Trading session not initialized')
				}

				const transferCall = buildErc20TransferCall(
					CHAIN_CONFIG.polygon.usdc as Address,
					eoaAddress as Address,
					amountRaw
				)
				const response = await executeWithRelayerFallback(
					relayClient,
					walletClient,
					[
						{
							to: transferCall.target,
							value: transferCall.value,
							data: transferCall.data,
							operation: OperationType.Call,
						},
					],
					'Move Polymarket funds to wallet'
				)
				await response.wait()
				return
			}

			const depositWalletAddress = deriveDepositWalletAddress(eoaAddress as Address)
			await ensureDepositWalletReady({ walletClient, publicClient, depositWalletAddress })
			await executeDepositWalletBatch(
				walletClient,
				[
					buildErc20TransferCall(
						CHAIN_CONFIG.polygon.usdc as Address,
						eoaAddress as Address,
						amountRaw
					),
				],
				depositWalletAddress
			)
		},
		[walletClient, publicClient, eoaAddress, polymarketSafeSourceAddress]
	)

	// Execute Polygon transaction via Polymarket relayer. This is only safe for
	// inbound Polymarket operations; outbound bridge approvals run from EOA.
	const executePolygonSteps = useCallback(
		async (steps: RelayStep[], relayClient: RelayClient | null): Promise<string | null> => {
			if (polymarketSafeSourceAddress) {
				// --- Safe path (legacy users) ---
				if (!relayClient) {
					throw new Error('Trading session not initialized')
				}

				const polygonTxs: SafeTransaction[] = []
				for (const step of steps) {
					for (const item of step.items) {
						if (item.data?.chainId === CHAIN_CONFIG.polygon.id) {
							const safeTx = relayTxToSafeTx(item.data)
							if (safeTx) polygonTxs.push(safeTx)
						}
					}
				}

				if (polygonTxs.length > 0) {
					const response = await executeWithRelayerFallback(
						relayClient,
						walletClient,
						polygonTxs,
						'Transfer stablecoins'
					)
					const result = (await response.wait()) as { txHash?: string } | null
					return result?.txHash || null
				}
				return null
			}

			// --- Deposit-wallet path (new users) ---
			if (!(walletClient && publicClient && eoaAddress)) {
				throw new Error('Wallet not connected')
			}
			try {
				const depositWalletAddress = deriveDepositWalletAddress(eoaAddress as Address)
				await ensureDepositWalletReady({ walletClient, publicClient, depositWalletAddress })

				// Collect Polygon-side calls from relay steps and send as a batch.
				// Each relay step item carries the raw calldata; encode as DepositWalletCall.
				const depositCalls = steps.flatMap(step =>
					step.items
						.filter(
							item =>
								item.data?.chainId === CHAIN_CONFIG.polygon.id && item.data?.to && item.data?.data
						)
						.map(item => ({
							target: (item.data?.to ?? '') as Address,
							value: item.data?.value || '0',
							data: (item.data?.data ?? '0x') as `0x${string}`,
						}))
				)

				if (depositCalls.length > 0) {
					const { transactionHash } = await executeDepositWalletBatch(
						walletClient,
						depositCalls,
						depositWalletAddress
					)
					return transactionHash
				}
				return null
			} catch (err) {
				captureApiError(err, {
					endpoint: 'polymarket_deposit_transfer',
					tags: { category: 'polymarket-deposit', flow: 'transfer' },
					extra: { depositWallet: eoaAddress, userId: undefined },
				})
				throw err
			}
		},
		[walletClient, publicClient, eoaAddress, polymarketSafeSourceAddress]
	)

	// Execute BSC transaction via sponsored EIP-7702 batch
	const executeBscSteps = useCallback(
		async (steps: RelayStep[]): Promise<string | null> => {
			if (!(evmAccount && eoaAddress)) {
				throw new Error('BSC wallet not available')
			}

			let hash: string | null = null

			// Collect all BSC operations into a batch
			const ops: { target: `0x${string}`; data: `0x${string}`; value?: bigint }[] = []

			for (const step of steps) {
				for (const item of step.items) {
					if (item.data?.chainId === CHAIN_CONFIG.bsc.id && item.data?.to && item.data?.data) {
						ops.push({
							target: item.data.to as `0x${string}`,
							data: item.data.data as `0x${string}`,
							value: item.data.value ? BigInt(item.data.value) : undefined,
						})
					}
				}
			}

			if (ops.length > 0) {
				hash = await sendSponsoredCalls({ chain: 'bsc', account: evmAccount, calls: ops })
			}

			return hash
		},
		[evmAccount, eoaAddress]
	)

	// Execute Base transaction via sponsored EIP-7702 batch
	const executeBaseSteps = useCallback(
		async (steps: RelayStep[]): Promise<string | null> => {
			if (!(evmAccount && eoaAddress)) {
				throw new Error('Base wallet not available')
			}

			const ops: { target: `0x${string}`; data: `0x${string}`; value?: bigint }[] = []

			for (const step of steps) {
				for (const item of step.items) {
					if (item.data?.chainId === CHAIN_CONFIG.base.id && item.data?.to && item.data?.data) {
						ops.push({
							target: item.data.to as `0x${string}`,
							data: item.data.data as `0x${string}`,
							value: item.data.value ? BigInt(item.data.value) : undefined,
						})
					}
				}
			}

			if (ops.length > 0) {
				return sendSponsoredCalls({ chain: 'base', account: evmAccount, calls: ops })
			}

			return null
		},
		[evmAccount, eoaAddress]
	)

	// Execute transfer
	const execute = useCallback(
		async (relayClient: RelayClient | null) => {
			if (!quote) {
				setError('No quote available')
				setStatus('error')
				return
			}

			setStatus('executing')
			setCurrentStepIndex(0)
			setTxHash(null)

			// Tracks whether the HL withdraw3 (final user-signed action) was
			// submitted. If the flow throws before this flips true, the
			// catch clears `hl-withdraw-in-progress` — otherwise a failed
			// USDH sell or spot→perp transfer would leave the 20-min
			// suppress-flag set and block auto-fund + auto-deposit even
			// though no funds were actually withdrawn.
			let hlWithdraw3Submitted = false

			try {
				let hash: string | null = null
				let handedOffToWatcher = false

				switch (quote.fromPlatform) {
					case 'kalshi':
						hash = await executeSolanaSteps(quote.steps)
						break
					case 'polymarket': {
						if (quote.toPlatform === 'polymarket') {
							hash = await executePolygonSteps(quote.steps, relayClient)
							break
						}
						// Suppress PolymarketEoaRescueBanner during this bridge —
						// sweep + Relay leg parks pUSD on the EOA briefly, which
						// the rescue banner otherwise mislabels as "stranded".
						// 15-min TTL covers Relay leg + Arbitrum landing +
						// auto-deposit watcher absorption window.
						try {
							localStorage.setItem(
								'polymarket-bridge-in-progress',
								String(Date.now() + 15 * 60 * 1000)
							)
						} catch {}
						const sweepAmount = BigInt(quote.raw.details.currencyIn.amount)
						await sweepPolymarketToEoa(sweepAmount, relayClient)
						// Wait for the EOA pUSD balance to reflect the sweep before
						// firing the Relay bridge userOp. Without this, Alchemy's
						// paymaster simulation can race against tx propagation and
						// revert with InsufficientBalance() (0xf4d678b8) because the
						// node it queries hasn't seen the sweep tx yet.
						if (publicClient && eoaAddress) {
							const POLYGON_PUSD = CHAIN_CONFIG.polygon.usdc as Address
							const startAt = Date.now()
							let baseline = 0n
							try {
								baseline = (await publicClient.readContract({
									address: POLYGON_PUSD,
									abi: erc20Abi,
									functionName: 'balanceOf',
									args: [eoaAddress as Address],
								})) as bigint
							} catch {}
							// Up to 20s of polling — fast nodes propagate in 1-3s,
							// slow ones may lag. Bail out anyway after that; if the
							// userOp still reverts the caller surfaces the error.
							while (Date.now() - startAt < 20_000) {
								try {
									const current = (await publicClient.readContract({
										address: POLYGON_PUSD,
										abi: erc20Abi,
										functionName: 'balanceOf',
										args: [eoaAddress as Address],
									})) as bigint
									if (current >= baseline + sweepAmount) break
								} catch {}
								await new Promise(r => setTimeout(r, 1_000))
							}
						}
						hash = await executePolygonEoaSteps(quote.steps)
						// Clear the bridge-in-progress flag once Relay leg landed.
						// Banner will resume normal behaviour (if any pUSD residue
						// remains on EOA after this, it really IS stranded and the
						// rescue prompt is appropriate).
						try {
							localStorage.removeItem('polymarket-bridge-in-progress')
						} catch {}
						break
					}
					case 'predictfun':
					case 'myriad':
						hash = await executeBscSteps(quote.steps)
						break
					case 'limitless':
						hash = await executeBaseSteps(quote.steps)
						break
					case 'hyperliquid': {
						// Multi-stage withdraw flow. HL withdraw3 only pulls from
						// PERP, but funds sit on SPOT (USDC or legacy USDH).
						// When perp is short, we run:
						//
						//   (0a) Sell USDH → USDC on @230 (agent-signed, silent)
						//   (0b) usdClassTransfer USDC spot → perp (user EIP-712)
						//   (1)  withdraw3 perp → Arbitrum EOA (user EIP-712)
						//   (2)  Poll Arbitrum balance until validator credit
						//   (3)  Hand off to useHyperliquidBridgeWatcher (background)
						//
						// Between (0a) and (0b) we re-fetch the spot balance because
						// the usdClassTransfer needs the post-sell USDC amount.
						if (!(publicClient && eoaAddress)) {
							throw new Error('Arbitrum wallet not ready')
						}
						const withdrawAmount = quote.inputAmount
						const requested = Number.parseFloat(withdrawAmount)
						// All HL endpoints authenticate via Privy bearer +
						// identity token (authFetch) — resolveUser →
						// requireAuthUser on the server. Same pattern as
						// useHyperliquidWithdraw.
						const fetchHlSplit = async () => {
							const r = await authFetch('/api/hyperliquid/balance')
							if (!r.ok) throw new Error(`balance ${r.status}`)
							return (await r.json()) as {
								perp?: { withdrawable: string }
								spot?: { usdc: string; usdh: string }
								withdrawable: string
							}
						}

						hlLog('[hl-bridge] phase=start', {
							requestedUsd: requested,
							destination: quote.toPlatform,
						})
						// Suppress auto-fund + auto-deposit watchers for the
						// WHOLE multi-step flow (sell USDH → spot USDC →
						// transfer to perp → withdraw3 → poll Arbitrum → Relay).
						// useHyperliquidWithdraw also sets this flag on its
						// own success, but by then the auto-fund effect can
						// already have raced our spot USDC and converted it
						// back to USDH, breaking the spot→perp transfer.
						// 20-min TTL covers worst-case 6-min Arbitrum credit
						// + Relay leg + slack.
						try {
							localStorage.setItem('hl-withdraw-in-progress', String(Date.now() + 20 * 60 * 1000))
						} catch {}
						let hlState = await fetchHlSplit()
						let perpAvailable = Number.parseFloat(
							hlState.perp?.withdrawable ?? hlState.withdrawable
						)
						const spotUsdc0 = Number.parseFloat(hlState.spot?.usdc ?? '0')
						const spotUsdh0 = Number.parseFloat(hlState.spot?.usdh ?? '0')
						hlLog('[hl-bridge] phase=balance-pre', {
							perp: perpAvailable,
							spotUsdc: spotUsdc0,
							spotUsdh: spotUsdh0,
							needsTopUp: perpAvailable < requested,
						})

						// Top-up only if perp is short by more than the pipeline's
						// slippage budget. ≤$0.05 shortfall vs requested is
						// covered by the pre-withdraw3 clamp below; entering
						// top-up just to add dust when nothing convertible
						// exists hits "no spot USDC" on stale balances.
						if (perpAvailable < requested - 0.05) {
							// Need to top up perp. Two sub-steps before withdraw.

							// Step 0a: sell spot USDH → USDC if needed. We sell the
							// FULL spot USDH (simplest correct behaviour) and the
							// usdClassTransfer in 0b moves the entire spot USDC
							// balance to perp. Any leftover after withdraw stays
							// on perp — user can re-withdraw or re-trade.
							if (spotUsdh0 > 0.01) {
								// HL spot orders need notional ≥ $10. The
								// /usdh-to-usdc agent sells at topBid × 0.995
								// (~$0.995 floor), so ~10.05 USDH suffices.
								// Keep this threshold in sync with the server
								// precheck in /api/hyperliquid/usdh-to-usdc.
								if (spotUsdh0 < 10.05) {
									throw new Error(
										`Spot USDH balance (${spotUsdh0.toFixed(2)}) is below HL's $10 minimum order. Deposit a bit more and retry, or wait for outcome settlements to top up perp directly.`
									)
								}
								hlLog('[hl-bridge] phase=sell-usdh-start', {
									usdhAmount: spotUsdh0.toFixed(2),
								})
								const sellResp = await authFetch('/api/hyperliquid/usdh-to-usdc', {
									method: 'POST',
									headers: { 'Content-Type': 'application/json' },
									body: JSON.stringify({ usdhAmount: spotUsdh0.toFixed(2) }),
								})
								if (!sellResp.ok) {
									const errBody = (await sellResp.json().catch(() => ({}))) as {
										error?: string
									}
									console.error('[hl-bridge] phase=sell-usdh FAILED', {
										status: sellResp.status,
										error: errBody.error,
									})
									throw new Error(
										`USDH → USDC conversion failed: ${errBody.error ?? `HTTP ${sellResp.status}`}`
									)
								}
								hlLog('[hl-bridge] phase=sell-usdh-submitted, polling spot USDC bump…')
								// Wait for spot USDC balance to reflect the sell.
								// Up to 30s — HL spot fills are fast (sub-second
								// usually) but the cache refresh + /info round-trip
								// adds latency.
								const startedSell = Date.now()
								let sellLanded = false
								while (Date.now() - startedSell < 30_000) {
									const fresh = await fetchHlSplit()
									const freshUsdc = Number.parseFloat(fresh.spot?.usdc ?? '0')
									if (freshUsdc > spotUsdc0 + 9) {
										hlState = fresh
										sellLanded = true
										hlLog('[hl-bridge] phase=sell-usdh DONE', {
											freshSpotUsdc: freshUsdc,
											elapsedMs: Date.now() - startedSell,
										})
										break
									}
									await new Promise(r => setTimeout(r, 2_000))
								}
								if (!sellLanded) {
									console.warn(
										'[hl-bridge] phase=sell-usdh timeout after 30s — spot USDC did not bump by $9+'
									)
								}
							}

							// Step 0b: usdClassTransfer spot USDC → perp. User
							// must sign EIP-712 (agent can't move user-class
							// funds). Move the FULL spot USDC so we don't have
							// to split — leftover after withdraw3 stays on perp.
							//
							// FLOOR to 2 decimals (not toFixed which rounds
							// half-up). HL reports balance to 8 decimals; if
							// the real balance is $19.137 then toFixed(2)
							// rounds to "$19.14" and the transfer is rejected
							// "Insufficient balance for token transfer" by
							// $0.003. Flooring guarantees we ask for at most
							// what we actually hold.
							const freshUsdcRaw = Number.parseFloat(hlState.spot?.usdc ?? '0')
							const freshUsdc = Math.floor(freshUsdcRaw * 100) / 100
							if (freshUsdc < 0.01) {
								throw new Error(
									'No spot USDC available after sell — try refreshing the page; if balance shows USDH only, contact support.'
								)
							}

							// Inline the init/confirm dance from
							// useHyperliquidPerpToSpot but with toPerp:true. We
							// can't call the hook here (this is inside a
							// callback), so duplicate the request shape.
							if (!evmAccount) throw new Error('Wallet not ready')
							hlLog('[hl-bridge] phase=transfer-spot-to-perp start', {
								rawSpotUsdc: freshUsdcRaw,
								flooredAmount: freshUsdc.toFixed(2),
							})
							const initRes = await authFetch('/api/hyperliquid/transfer/init', {
								method: 'POST',
								headers: { 'Content-Type': 'application/json' },
								body: JSON.stringify({ amount: freshUsdc.toFixed(2), toPerp: true }),
							})
							if (!initRes.ok) {
								const e = (await initRes.json().catch(() => ({}))) as { error?: string }
								console.error('[hl-bridge] transfer/init FAILED', {
									status: initRes.status,
									error: e.error,
								})
								throw new Error(e.error || `transfer init ${initRes.status}`)
							}
							const init = (await initRes.json()) as {
								typedData: {
									domain: {
										name: string
										version: string
										chainId: number
										verifyingContract: Address
									}
									types: Record<string, Array<{ name: string; type: string }>>
									primaryType: string
									message: Record<string, unknown>
								}
								nonce: number
								amount: string
								toPerp: boolean
							}
							hlLog('[hl-bridge] transfer/init OK, prompting signTypedData…')
							const transferSig = await evmAccount.signTypedData({
								// biome-ignore lint/suspicious/noExplicitAny: typed-data shape
								domain: init.typedData.domain as any,
								// biome-ignore lint/suspicious/noExplicitAny: same
								types: init.typedData.types as any,
								// biome-ignore lint/suspicious/noExplicitAny: same
								primaryType: init.typedData.primaryType as any,
								// biome-ignore lint/suspicious/noExplicitAny: same
								message: init.typedData.message as any,
							})
							hlLog('[hl-bridge] user signed transfer, calling /confirm…')
							const confirmRes = await authFetch('/api/hyperliquid/transfer/confirm', {
								method: 'POST',
								headers: { 'Content-Type': 'application/json' },
								body: JSON.stringify({
									amount: init.amount,
									toPerp: init.toPerp,
									nonce: init.nonce,
									signature: transferSig,
								}),
							})
							if (!confirmRes.ok) {
								const e = (await confirmRes.json().catch(() => ({}))) as {
									error?: string
								}
								console.error('[hl-bridge] transfer/confirm FAILED', {
									status: confirmRes.status,
									error: e.error,
								})
								throw new Error(e.error || `transfer confirm ${confirmRes.status}`)
							}
							hlLog('[hl-bridge] transfer/confirm OK, polling perp bump…')

							// Wait for the transfer to LAND, not for perp to hit
							// `requested`. When MAX includes USDH sold at
							// topBid*0.995, perp ends up ~0.5% short of
							// `requested` — gating on `requested - 0.01` would
							// always timeout. Detect arrival via the actual
							// transfer amount; the post-loop clamp sends what
							// landed.
							const perpBeforeTransfer = perpAvailable
							const startedTransfer = Date.now()
							const targetPerp = perpBeforeTransfer + freshUsdc - 0.05
							while (Date.now() - startedTransfer < 30_000) {
								const fresh = await fetchHlSplit()
								perpAvailable = Number.parseFloat(fresh.perp?.withdrawable ?? fresh.withdrawable)
								if (perpAvailable >= targetPerp) break
								await new Promise(r => setTimeout(r, 2_000))
							}
							if (perpAvailable < targetPerp) {
								console.error('[hl-bridge] phase=transfer-spot-to-perp TIMEOUT', {
									finalPerp: perpAvailable,
									targetPerp,
									elapsedMs: Date.now() - startedTransfer,
								})
								throw new Error(
									`Perp top-up didn't reflect within 30s (perp = ${perpAvailable.toFixed(2)}, expected at least ${targetPerp.toFixed(2)}). Wait a moment and retry — balance may still be settling.`
								)
							}
							hlLog('[hl-bridge] phase=transfer-spot-to-perp DONE', {
								perpAvailable,
								elapsedMs: Date.now() - startedTransfer,
							})
						}

						const ARB_USDC = CHAIN_CONFIG.arbitrum.usdc as Address
						let baseline = 0n
						try {
							baseline = (await arbitrumReadClient.readContract({
								address: ARB_USDC,
								abi: erc20Abi,
								functionName: 'balanceOf',
								args: [eoaAddress as Address],
							})) as bigint
						} catch {
							// Baseline read is best-effort — defaults to 0n, which is
							// safe (we just over-count and wait an extra second).
						}
						// Re-read perp one more time and clamp the withdraw to the
						// actual on-perp number. The multi-step pipeline (USDH
						// sold at topBid*0.995 + spot→perp transfer quantized to
						// 2 decimals) loses a few cents to slippage and flooring,
						// so withdraw3 against the user-quoted amount fails with
						// "Insufficient balance for withdrawal" by ~$0.01.
						const preWithdrawSnap = await fetchHlSplit()
						const perpAtWithdraw = Number.parseFloat(
							preWithdrawSnap.perp?.withdrawable ?? preWithdrawSnap.withdrawable
						)
						const clampedWithdraw = Math.min(
							Number.parseFloat(withdrawAmount),
							perpAtWithdraw
						).toFixed(2)
						if (Number.parseFloat(clampedWithdraw) < 2) {
							throw new Error(
								`Only $${perpAtWithdraw.toFixed(2)} on perp after sell + transfer (lost to slippage). Below $2 HL minimum withdraw.`
							)
						}
						hlLog('[hl-bridge] phase=withdraw3 start', {
							requested: withdrawAmount,
							perpAtWithdraw,
							clamped: clampedWithdraw,
							destination: eoaAddress,
							arbBaselineRaw: baseline.toString(),
						})
						await hyperliquidWithdraw.mutateAsync({
							amount: clampedWithdraw,
							destination: eoaAddress as Address,
						})
						hlWithdraw3Submitted = true
						hlLog('[hl-bridge] phase=withdraw3 submitted, handing off to background watcher')
						// withdraw3 amount has 2-decimal precision; HL bridge net
						// fee is $1, so the user receives `amount - 1` on Arbitrum.
						// Use clampedWithdraw (actual sent amount), not the
						// original quoted withdrawAmount, otherwise we'd poll
						// for $0.01 more than will ever land.
						const expectedRaw = BigInt(Math.floor((Number.parseFloat(clampedWithdraw) - 1) * 1e6))

						// Hand off the remaining steps (Arbitrum credit poll +
						// fresh Relay quote + sponsored userOp) to the
						// background watcher. The user-signed work is done;
						// validators credit ~3-4 min later regardless of what
						// the UI is doing, and the Relay leg has no user
						// prompts. Blocking the modal on 4+ min of polling
						// was the worst UX in the whole flow.
						const destinationConfig = getPlatformChainConfig(quote.toPlatform)
						const destinationRecipient = getUserAddress(quote.toPlatform)
						if (!destinationRecipient) {
							throw new Error(
								`Hyperliquid withdraw3 already submitted, but no destination wallet resolved for ${quote.toPlatform}. Funds will land on Arbitrum (${eoaAddress}) — recover via the portfolio "Withdraw stuck" banner.`
							)
						}
						persistActiveBridge({
							startedAt: Date.now(),
							destination: quote.toPlatform,
							destChainId: destinationConfig.chainId,
							destCurrency: destinationConfig.currency,
							destRecipient: destinationRecipient,
							arbBaselineRaw: baseline.toString(),
							expectedArbAmountRaw: expectedRaw.toString(),
							amountFormatted: (Number.parseFloat(clampedWithdraw) - 1).toFixed(2),
						})
						// Persist expected amount separately so the stranded-withdraw
						// banner can cap its recovery bridge even after the active-bridge
						// state expires (30min TTL). Without this, the banner bridges the
						// FULL Arbitrum balance including pre-existing USDC.
						try {
							localStorage.setItem('hl-expected-withdraw-arb-raw', expectedRaw.toString())
						} catch {}
						hlLog('[hl-bridge] persisted active-bridge state for watcher takeover')
						toast.loading('Validators crediting Arbitrum (~3-4 min)…', {
							id: 'hl-bridge-watcher',
							description: `Funds will arrive on ${quote.toPlatform} automatically`,
							// Hard cap so a watcher hang doesn't strand the toast.
							duration: 8 * 60 * 1000,
						})
						// Hand off to useHyperliquidBridgeWatcher. Leaving `hash`
						// as null prevents the success view from rendering a
						// dead explorer link (the watcher's userOp tx hash
						// doesn't exist yet).
						handedOffToWatcher = true
						break
					}
				}

				// Skip the relay-completion poll when the HL withdraw flow
				// handed off to the watcher — the original quote's relay
				// requestIds are stale (the watcher will fetch a fresh quote).
				if (handedOffToWatcher) {
					setStatus('success')
					invalidateMarketBalances(queryClient, ['polymarket', 'kalshi', 'predictfun', 'limitless'])
					return
				}

				// Poll for relay completion
				for (const step of quote.steps) {
					if (step.requestId) {
						let attempts = 0
						while (attempts < 60) {
							try {
								const statusResult = await getRelayStatus(step.requestId)
								if (statusResult.status === 'success') {
									break
								}
								if (statusResult.status === 'failure') {
									console.warn('[Transfer] Relay failed:', statusResult.data?.failReason)
									break
								}
							} catch {
								// Status endpoint might not be available yet
							}
							await new Promise(r => setTimeout(r, 2000))
							attempts++
						}
					}
				}

				setTxHash(hash)
				setStatus('success')

				invalidateMarketBalances(queryClient, ['polymarket', 'kalshi', 'predictfun', 'limitless'])
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Transfer failed'
				setError(message)
				setStatus('error')
				console.error('[Transfer] Error:', err)
				// Clear the suppress-flag if withdraw3 never went out —
				// otherwise a failed USDH sell / spot→perp transfer leaves
				// auto-fund and auto-deposit blocked for 20min for no
				// reason (no funds were withdrawn).
				if (quote.fromPlatform === 'hyperliquid' && !hlWithdraw3Submitted) {
					try {
						localStorage.removeItem('hl-withdraw-in-progress')
					} catch {}
				}
			}
		},
		[
			quote,
			executeSolanaSteps,
			executePolygonSteps,
			executePolygonEoaSteps,
			sweepPolymarketToEoa,
			executeBscSteps,
			executeBaseSteps,
			hyperliquidWithdraw,
			publicClient,
			eoaAddress,
			queryClient,
			getUserAddress,
		]
	)

	const reset = useCallback(() => {
		setStatus('idle')
		setQuote(null)
		setCurrentStepIndex(0)
		setError(null)
		setTxHash(null)
	}, [])

	return {
		// Balances
		polymarketBalance,
		kalshiBalance,
		predictfunBalance,
		getBalance,
		// Quote
		quote,
		fetchQuote,
		// Execution
		execute,
		// Status
		status,
		currentStepIndex,
		error,
		txHash,
		// Actions
		reset,
	}
}
